import React, { useState, useEffect, useRef } from 'react';
import { useNotifications } from '../../hooks/useNotifications';
import NotificationPopup from './NotificationPopup';
import { Bell } from 'lucide-react';

const NotificationBell = ({ 
  showPopup = true,
  popupDuration = 5000,
  className = "",
  menuPosition = "right" // "right" o "left"
}) => {
  const [popupNotifications, setPopupNotifications] = useState([]);
  const [lastUnreadCount, setLastUnreadCount] = useState(0);
  const [processedNotificationIds, setProcessedNotificationIds] = useState(new Set());
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const bellRef = useRef(null);
  const menuRef = useRef(null);
  
  const {
    notifications,
    loading,
    error,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useNotifications();

  // Debug: log per verificare se il componente si carica
  // console.log('🔔 NotificationBell loaded:', { notifications, loading, error, unreadCount });

  // Gestisce le nuove notifiche per il popup
  useEffect(() => {
    if (showPopup && notifications.length > 0) {
      // Trova tutte le notifiche non lette che non sono ancora state processate
      const unreadNotifications = notifications.filter(n => !n.read_at);
      const newNotifications = unreadNotifications.filter(n => !processedNotificationIds.has(n.id));

      if (newNotifications.length > 0) {
        
        // Mostra popup per le nuove notifiche
        newNotifications.forEach(notification => {
          setPopupNotifications(prev => [...prev, notification]);
          
          // Marca come processata
          setProcessedNotificationIds(prev => new Set([...prev, notification.id]));
        });
      }

      setLastUnreadCount(unreadCount);
    }
  }, [notifications, unreadCount, showPopup, processedNotificationIds]);

  // Gestisce la chiusura del popup
  const handleClosePopup = (notificationId) => {
    setPopupNotifications(prev => 
      prev.filter(n => n.id !== notificationId)
    );
  };

  // Gestisce la chiusura del menu quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && 
          bellRef.current && 
          menuRef.current && 
          !bellRef.current.contains(event.target) && 
          !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Gestisce il segna come letta dal popup
  const handleMarkAsReadFromPopup = async (notificationId) => {
    await markAsRead(notificationId);
    handleClosePopup(notificationId);
  };

  return (
    <div className="relative" ref={bellRef}>
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors relative"
        aria-label="Notifiche"
      >
        <Bell className="h-5 w-5 text-gray-600 dark:text-gray-300" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 bg-red-500 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      {isMenuOpen && (
        <div ref={menuRef} className={`absolute ${menuPosition === 'left' ? 'left-0' : 'right-0'} mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50`}>
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notifiche</h3>
              {unreadCount > 0 && (
                <button
                  onClick={() => {
                    if (window.confirm('Segnare tutte le notifiche come lette?')) {
                      markAllAsRead();
                    }
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900"
                >
                  Segna tutte lette
                </button>
              )}
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                Caricamento...
              </div>
            ) : error ? (
              <div className="p-4 text-center text-red-500">
                Errore: {error}
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                Nessuna notifica
              </div>
            ) : (
              notifications.map(notification => (
                <div key={notification.id} className="p-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        {notification.title}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                        {new Date(notification.created_at).toLocaleString('it-IT')}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-2">
                      {!notification.read_at && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900"
                        >
                          Segna letta
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (window.confirm('Sei sicuro di voler cancellare questa notifica?')) {
                            deleteNotification(notification.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-800 text-xs px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900"
                      >
                        Cancella
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Popup notifiche */}
      {showPopup && popupNotifications.map((notification, index) => (
        <div
          key={`${notification.id}-${index}`}
          className="fixed top-4 right-4 z-50"
          style={{
            transform: `translateY(${index * 180}px)`,
            zIndex: 50 + index
          }}
        >
          <NotificationPopup
            notification={notification}
            onClose={() => handleClosePopup(notification.id)}
            onMarkAsRead={handleMarkAsReadFromPopup}
            autoClose={true}
            duration={popupDuration}
          />
        </div>
      ))}
    </div>
  );
};

export default NotificationBell;